/** Volatile signup metadata. It never stores or joins a visitor identifier. */
export type SignupAttribution = {
  v: 1;
  landing_consent: true;
  issued_at: number;
  source: string;
  medium?: string;
  campaign?: string;
};

const PARAM = "signup_attribution";
const LABEL = /^[a-z0-9][a-z0-9._-]{0,79}$/i;
const FIELDS = new Set([
  "v",
  "landing_consent",
  "issued_at",
  "source",
  "medium",
  "campaign",
]);
let pending: SignupAttribution | undefined;

function sanitize(value: unknown, now: number): SignupAttribution | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return;
  const data = value as Record<string, unknown>;
  if (
    Object.keys(data).some((key) => !FIELDS.has(key)) ||
    data.v !== 1 ||
    data.landing_consent !== true ||
    !Number.isInteger(data.issued_at) ||
    now - Number(data.issued_at) > 1800 ||
    Number(data.issued_at) - now > 60
  )
    return;
  for (const key of ["source", "medium", "campaign"]) {
    const label = data[key];
    if (key !== "source" && label === undefined) continue;
    if (typeof label !== "string" || LABEL.exec(label)?.[0] !== label) return;
  }
  return { ...data } as SignupAttribution;
}

export function clearSignupAttribution(): void {
  pending = undefined;
}

/** Strip the carrier before SDKs read the URL, and accept only our landing origin. */
export function captureSignupAttribution(
  href = window.location.href,
  referrer = document.referrer,
  now = Math.floor(Date.now() / 1000),
): void {
  try {
    const url = new URL(href);
    const values = url.searchParams.getAll(PARAM);
    if (!values.length) return;
    const raw = values.length === 1 ? values[0] : undefined;
    clearSignupAttribution();
    url.searchParams.delete(PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    const origin = new URL(referrer).origin;
    if (
      url.origin !== "https://space.denker.ai" ||
      !["https://denker.ai", "https://www.denker.ai"].includes(origin) ||
      !["/auth/register", "/auth/login"].includes(url.pathname) ||
      !raw ||
      raw.length > 512
    )
      return;
    pending = sanitize(JSON.parse(raw), now);
  } catch {
    // Optional metadata must never prevent signup, including unavailable history.
    clearSignupAttribution();
  }
}

/** The landing handoff for a sign-up request, if one is still fresh. */
export function signupAttributionFields(): {
  signup_attribution?: SignupAttribution;
} {
  pending = sanitize(pending, Math.floor(Date.now() / 1000));
  return pending ? { signup_attribution: { ...pending } } : {};
}

/** Query entry for the Google sign-up redirect; reading it keeps the handoff. */
export function signupAttributionQuery(): [string, string][] {
  const payload = signupAttributionFields().signup_attribution;
  return payload ? [[PARAM, JSON.stringify(payload)]] : [];
}
